import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { Combobox } from "../components/SharedComponents";
import { getQAChecks, saveQAChecks, getQAChecksVersions, rollbackQAChecks, suggestCheckQuestion, getModalities, getQACheckOverrideCounts, refineQACheck } from "../lib/api";

/* ═══════════════════════════════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════════════════════════════ */
const AUDIT_TYPES = ["Technologist", "Interpreting_Physician"];
const CATEGORIES = ["Documentation", "Communication", "Clinical Protocol"];
const BASE_SOURCES = [
  "Usmon Chat Log",
  "Interpretation Report",
  "Technical Report",
  "Usmon Screenshots",
];

const SOURCE_BADGE = {
  "Usmon Chat Log":        { short: "CHAT", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  "Interpretation Report": { short: "IP",   color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
  "Technical Report":      { short: "TECH", color: "#0891b2", bg: "#ecfeff", border: "#a5f3fc" },
  "Usmon Screenshots":     { short: "SCRN", color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
};

const EMPTY_CHECK = {
  Id: "", AuditType: "Technologist", CategoryValue: "Documentation",
  RequiredSources: "Usmon Chat Log", CriteriaValue: "", Criteria_PrAppSortValue: "",
  Question: "", DisplayQuestion: "", ModalityValue: "", EventDependent: "",
  StatusValue: "Active",
  DefaultValue: "Default", Max_points: "1", MappedIPReportCategory: "",
  SensitiveEvidence: "N",
};

const IP_CATEGORIES = ["Availability", "Communication", "Documentation", "Event Handling"];

/* ═══════════════════════════════════════════════════════════════════
   ICONS
   ═══════════════════════════════════════════════════════════════════ */
const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <circle cx="7" cy="7" r="4.5"/><line x1="10.5" y1="10.5" x2="14" y2="14"/>
  </svg>
);
const SparkleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M8 2v2m0 8v2M2 8h2m8 0h2M4.5 4.5l1 1m5 5l1 1M4.5 11.5l1-1m5-5l1-1"/><circle cx="8" cy="8" r="2.5"/>
  </svg>
);
const ChevronIcon = ({ open }) => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
    style={{ transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "none" }}>
    <path d="M4 6l4 4 4-4"/>
  </svg>
);

/* ═══════════════════════════════════════════════════════════════════
   SOURCE BADGE COMPONENT
   ═══════════════════════════════════════════════════════════════════ */
function SourceBadges({ sources }) {
  if (!sources) return null;
  const parts = sources.includes("/") ? sources.split("/") : [sources];
  return (
    <span style={{ display: "inline-flex", gap: 3 }}>
      {parts.map((s, i) => {
        const trimmed = s.trim();
        const badge = SOURCE_BADGE[trimmed] || { short: trimmed.slice(0, 4).toUpperCase(), color: "#94a3b8", bg: "#f8fafc", border: "#e2e8f0" };
        return (
          <span key={i} title={trimmed} style={{
            fontSize: 9, fontFamily: "var(--font-mono)", fontWeight: 600, padding: "1px 5px", borderRadius: 3,
            background: badge.bg, color: badge.color, border: `1px solid ${badge.border}`,
          }}>{badge.short}</span>
        );
      })}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   AI SUGGESTION PANEL
   ═══════════════════════════════════════════════════════════════════ */
function AISuggestPanel({ question, onUseSuggestion, onClose }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(-1);

  useEffect(() => {
    setLoading(true);
    setError(null);
    suggestCheckQuestion(question, "")
      .then(data => {
        setSuggestions(data.suggestions || []);
        setLoading(false);
      })
      .catch(e => {
        setError(e?.message || "Failed to get suggestions");
        setLoading(false);
      });
  }, [question]);

  return (
    <div style={{ marginTop: 12, padding: 14, border: "1px solid #CECBF6", borderRadius: 8, background: "#FAFAFF" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#EEEDFE", display: "flex", alignItems: "center", justifyContent: "center", color: "#534AB7" }}>
            <SparkleIcon />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 500, color: "#3C3489" }}>AI suggestions</div>
            <div style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "#534AB7" }}>
              {loading ? "Analyzing..." : `${suggestions.length} improved versions`}
            </div>
          </div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#534AB7", fontSize: 14, padding: 4 }}>✕</button>
      </div>

      <div style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", marginBottom: 4 }}>Your original</div>
      <div style={{ fontSize: 12, color: "var(--text-secondary)", padding: "6px 10px", background: "var(--gray-50, #f9fafb)", borderRadius: 6, textDecoration: "line-through", opacity: 0.6, marginBottom: 12 }}>
        {question}
      </div>

      {loading && (
        <div style={{ padding: 24, textAlign: "center", color: "#534AB7", fontFamily: "var(--font-mono)", fontSize: 12 }}>
          Generating improvements...
        </div>
      )}

      {error && (
        <div style={{ padding: 12, background: "#fef2f2", borderRadius: 6, color: "#dc2626", fontSize: 12 }}>
          {error}
        </div>
      )}

      {!loading && !error && suggestions.map((sug, i) => (
        <div key={i} onClick={() => setSelected(i)} style={{
          padding: "10px 12px", border: `1px solid ${selected === i ? "#534AB7" : "var(--border)"}`,
          borderRadius: 8, marginBottom: 8, cursor: "pointer", background: selected === i ? "#EEEDFE" : "var(--white)",
          transition: "border-color 0.15s", display: "flex", gap: 10, alignItems: "flex-start",
        }}>
          <div style={{
            width: 16, height: 16, borderRadius: "50%", border: `1.5px solid ${selected === i ? "#534AB7" : "var(--border)"}`,
            flexShrink: 0, marginTop: 2, display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {selected === i && <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#534AB7" }} />}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)", lineHeight: 1.4 }}>{sug.question}</div>
            <div style={{ fontSize: 11, color: "#534AB7", marginTop: 4, lineHeight: 1.4 }}>{sug.reason}</div>
            {i === 0 && <span style={{ fontSize: 9, fontFamily: "var(--font-mono)", padding: "2px 6px", borderRadius: 3, background: "#E1F5EE", color: "#085041", marginTop: 4, display: "inline-block" }}>Recommended</span>}
          </div>
        </div>
      ))}

      {!loading && !error && (
        <button onClick={() => selected >= 0 && onUseSuggestion(suggestions[selected].question)}
          disabled={selected < 0}
          style={{
            padding: "6px 16px", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: selected >= 0 ? "pointer" : "not-allowed",
            background: selected >= 0 ? "#534AB7" : "var(--gray-200, #e5e7eb)", color: selected >= 0 ? "white" : "var(--text-tertiary)", marginTop: 4,
          }}>
          Use selected suggestion
        </button>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MULTI-SOURCE SELECTOR (pill toggles using existing badge shortcodes)
   ═══════════════════════════════════════════════════════════════════ */
function MultiSourceSelector({ value, onChange }) {
  const selected = new Set((value || "").split("/").map(s => s.trim()).filter(Boolean));

  const toggle = (source) => {
    const next = new Set(selected);
    if (next.has(source)) next.delete(source);
    else next.add(source);
    onChange(BASE_SOURCES.filter(s => next.has(s)).join("/"));
  };

  const arr = Array.from(selected);
  let semantics = null;
  if (arr.length >= 2) {
    semantics = arr.includes("Interpretation Report")
      ? { label: "All required (comparison check)", color: "#0891b2", bg: "#ecfeff", border: "#a5f3fc" }
      : { label: "Any sufficient", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" };
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {BASE_SOURCES.map(src => {
          const isOn = selected.has(src);
          const badge = SOURCE_BADGE[src];
          return (
            <button
              key={src}
              type="button"
              onClick={() => toggle(src)}
              title={src}
              style={{
                padding: "5px 10px",
                border: `1px solid ${isOn ? badge.border : "var(--border)"}`,
                borderRadius: 5,
                fontSize: 11,
                fontFamily: "var(--font-mono)",
                fontWeight: 600,
                cursor: "pointer",
                background: isOn ? badge.bg : "var(--white)",
                color: isOn ? badge.color : "var(--text-tertiary)",
                opacity: isOn ? 1 : 0.6,
                transition: "opacity 0.15s, background 0.15s",
              }}>
              {badge.short}
            </button>
          );
        })}
      </div>
      {semantics && (
        <div style={{ marginTop: 6 }}>
          <span style={{
            fontSize: 10, fontFamily: "var(--font-mono)",
            padding: "2px 6px", borderRadius: 3,
            background: semantics.bg, color: semantics.color,
            border: `1px solid ${semantics.border}`,
          }}>
            {semantics.label}
          </span>
        </div>
      )}
      {arr.length === 0 && (
        <div style={{ marginTop: 6, fontSize: 10, fontFamily: "var(--font-mono)", color: "#dc2626" }}>
          At least one source required
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   EDIT FORM
   ═══════════════════════════════════════════════════════════════════ */
function CheckEditForm({ check, onSave, onCancel, isNew, modalityOptions = [], criteriaOptions = [] }) {
  const [form, setForm] = useState({ ...check });
  const [showAI, setShowAI] = useState(false);

  const update = (field, value) => setForm(f => ({ ...f, [field]: value }));

  return (
    <div style={{ padding: 16, background: "var(--gray-50, #f9fafb)", borderBottom: "1px solid var(--border)" }}>
      {/* Question + AI suggest */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 4 }}>
          Prompt sent to the model
        </label>
        <textarea value={form.Question} onChange={e => update("Question", e.target.value)}
          style={{ width: "100%", padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 13, minHeight: 60, resize: "vertical", fontFamily: "var(--font-sans)", background: "var(--white)", color: "var(--text-primary)", boxSizing: "border-box" }} />
        {form.Question.length > 5 && !showAI && (
          <button onClick={() => setShowAI(true)} style={{
            marginTop: 6, padding: "5px 12px", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 500,
            cursor: "pointer", background: "#EEEDFE", color: "#3C3489", display: "inline-flex", alignItems: "center", gap: 6,
          }}>
            <SparkleIcon /> Improve with AI
          </button>
        )}
        {showAI && (
          <AISuggestPanel
            question={form.Question}
            onUseSuggestion={q => { update("Question", q); setShowAI(false); }}
            onClose={() => setShowAI(false)}
          />
        )}
      </div>

      {/* Reporting wording. Question goes to the model; this is what people
          read. Left blank, reports fall back to the Question text above. */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 4 }}>
          Wording shown on reports
        </label>
        <textarea value={form.DisplayQuestion || ""} onChange={e => update("DisplayQuestion", e.target.value)}
          placeholder={form.Question ? form.Question : "Defaults to the question above"}
          style={{ width: "100%", padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 13, minHeight: 44, resize: "vertical", fontFamily: "var(--font-sans)", background: "var(--white)", color: "var(--text-primary)", boxSizing: "border-box" }} />
        <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 4 }}>
          The question above is what the model is asked, so it can be as long
          and specific as it needs to be. This is the short version shown on
          reports and on the case page. Leave it empty and the question is used
          as-is.
        </div>
      </div>

      {/* Field grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 4 }}>Audit type</label>
          <select value={form.AuditType} onChange={e => update("AuditType", e.target.value)}
            style={{ width: "100%", padding: "6px 8px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12, background: "var(--white)", color: "var(--text-primary)" }}>
            {AUDIT_TYPES.map(t => <option key={t} value={t}>{t === "Interpreting_Physician" ? "Interpreting Physician" : t}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 4 }}>Category</label>
          <select value={form.CategoryValue} onChange={e => update("CategoryValue", e.target.value)}
            style={{ width: "100%", padding: "6px 8px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12, background: "var(--white)", color: "var(--text-primary)" }}>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        {form.AuditType === "Interpreting_Physician" && (
          <div>
            <label style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 4 }}>
              IP report category <span style={{ color: "#7c3aed", textTransform: "none", letterSpacing: 0 }}>(IP only)</span>
            </label>
            <select value={form.MappedIPReportCategory || ""} onChange={e => update("MappedIPReportCategory", e.target.value)}
              style={{ width: "100%", padding: "6px 8px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12, background: "var(--white)", color: "var(--text-primary)" }}>
              <option value="">(Unmapped)</option>
              {IP_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
        )}
        <div>
          <label style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 4 }}>Criteria group</label>
          <Combobox
            value={form.CriteriaValue}
            onChange={val => update("CriteriaValue", val)}
            options={criteriaOptions}
            placeholder="Type or pick a group..."
          />
        </div>
        <div>
          <label style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 4 }}>Required sources</label>
          <MultiSourceSelector
            value={form.RequiredSources}
            onChange={val => update("RequiredSources", val)}
          />
        </div>
        <div>
          <label style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 4 }}>Modality</label>
          <select value={form.ModalityValue} onChange={e => update("ModalityValue", e.target.value)}
            style={{ width: "100%", padding: "6px 8px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12, background: "var(--white)", color: "var(--text-primary)" }}>
            <option value="">(All modalities)</option>
            {modalityOptions.map(m => (
                  <option key={m.Code} value={m.Code}>{m.DisplayName || m.Code}</option>
                ))}
            {/* Render legacy values not in current modality list so existing data isn't lost */}
            {form.ModalityValue && !modalityOptions.find(m => m.Code === form.ModalityValue) &&
             form.ModalityValue.toUpperCase() !== "ALL" && (
              <option value={form.ModalityValue}>{form.ModalityValue} (legacy)</option>
            )}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 4 }}>Sort order</label>
          <input type="number" min="1" value={form.Criteria_PrAppSortValue} onChange={e => update("Criteria_PrAppSortValue", e.target.value)}
            style={{ width: "100%", padding: "6px 8px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12, background: "var(--white)", color: "var(--text-primary)", boxSizing: "border-box" }} />
        </div>
        <div>
          <label style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 4 }}>Max points</label>
          <input type="number" min="1" value={form.Max_points} onChange={e => update("Max_points", e.target.value)}
            style={{ width: "100%", padding: "6px 8px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12, background: "var(--white)", color: "var(--text-primary)", boxSizing: "border-box" }} />
        </div>
        <div>
          <label style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 4 }}>Event dependent</label>
          <select value={form.EventDependent} onChange={e => update("EventDependent", e.target.value)}
            style={{ width: "100%", padding: "6px 8px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12, background: "var(--white)", color: "var(--text-primary)" }}>
            <option value="">(None)</option><option value="Yes">Yes</option>
          </select>
        </div>
      </div>

      {/* Stored as the CSV's Y/N, not a boolean, so the column stays readable
          in a spreadsheet alongside the other Y/N columns. */}
      <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 14,
                      padding: "10px 12px", border: "1px solid var(--border)",
                      borderRadius: 6, background: "var(--gray-50, #f9fafb)",
                      cursor: "pointer" }}>
        <input type="checkbox" style={{ marginTop: 2, flexShrink: 0 }}
          checked={String(form.SensitiveEvidence || "").toUpperCase() === "Y"}
          onChange={e => update("SensitiveEvidence", e.target.checked ? "Y" : "N")} />
        <span>
          <span style={{ fontSize: 12, fontWeight: 500, color: "var(--text-primary)" }}>
            Evidence contains a patient identifier
          </span>
          <span style={{ display: "block", fontSize: 11, color: "var(--text-tertiary)", marginTop: 2 }}>
            Leaves this check collapsed when a reviewer presses Expand all on the
            audit page, so a name or date of birth is not swept into a shared PDF.
            It can still be opened on its own. The question above is unaffected —
            only the evidence that answers it.
          </span>
        </span>
      </label>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
        <button onClick={onCancel} style={{ padding: "6px 16px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12, cursor: "pointer", background: "var(--white)", color: "var(--text-secondary)" }}>Cancel</button>
        <button onClick={() => onSave(form)} style={{ padding: "6px 16px", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: "pointer", background: "var(--blue-800, #1d4ed8)", color: "white" }}>
          {isNew ? "Add check" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   VERSION HISTORY MODAL
   ═══════════════════════════════════════════════════════════════════ */
function VersionHistory({ onClose, onRollback }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rolling, setRolling] = useState(null);

  useEffect(() => {
    getQAChecksVersions()
      .then(data => { setVersions(data.versions || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleRollback = async (versionId) => {
    if (!window.confirm("Restore this version? Current checks will be overwritten.")) return;
    setRolling(versionId);
    try {
      await rollbackQAChecks(versionId);
      onRollback();
    } catch (e) {
      alert("Rollback failed: " + (e?.message || String(e)));
    }
    setRolling(null);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "var(--white)", borderRadius: 12, padding: 24, width: 480, maxHeight: "70vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Version history</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "var(--text-tertiary)" }}>✕</button>
        </div>

        {loading && <div style={{ padding: 24, textAlign: "center", color: "var(--text-tertiary)", fontSize: 12 }}>Loading versions...</div>}

        {!loading && versions.length === 0 && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--text-tertiary)", fontSize: 12 }}>No previous versions found</div>
        )}

        {!loading && versions.map((v, i) => (
          <div key={v.versionId || i} style={{
            padding: "12px 14px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 12,
          }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: i === 0 ? "#16a34a" : "var(--border)", flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text-primary)" }}>
                {i === 0 ? "Current version" : `Version ${versions.length - i}`}
              </div>
              <div style={{ fontSize: 11, color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>
                {v.lastModified ? new Date(v.lastModified).toLocaleString() : "Unknown date"}
                {v.checkCount && ` · ${v.checkCount} checks`}
              </div>
            </div>
            {i > 0 && (
              <button onClick={() => handleRollback(v.versionId)} disabled={rolling === v.versionId}
                style={{ padding: "4px 10px", border: "1px solid var(--border)", borderRadius: 5, fontSize: 10, fontFamily: "var(--font-mono)", cursor: "pointer", background: "var(--white)", color: "var(--text-secondary)" }}>
                {rolling === v.versionId ? "Restoring..." : "Restore"}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   REFINE FROM OVERRIDES MODAL
   ═══════════════════════════════════════════════════════════════════ */
function RefineModal({ check, onClose, onAccept }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(-1);
  const [showExamples, setShowExamples] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    refineQACheck(check.Id)
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e?.message || String(e)); setLoading(false); });
  }, [check.Id]);

  const variantLabel = (i, total) => {
    if (total === 3) return ["Conservative", "Moderate", "Aggressive"][i] || `Variant ${i + 1}`;
    return `Variant ${i + 1}`;
  };
  const variantStyles = [
    { bg: "#ecfdf5", color: "#047857", border: "#a7f3d0" },
    { bg: "#fffbeb", color: "#b45309", border: "#fde68a" },
    { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "var(--white)", borderRadius: 12, padding: 24, width: 720, maxHeight: "85vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text-primary)" }}>Refine check #{check.Id} from reviewer overrides</div>
            <div style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", marginTop: 2 }}>
              {check.AuditType === "Interpreting_Physician" ? "Interpreting Physician" : check.AuditType} · {check.CategoryValue}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "var(--text-tertiary)", padding: 4 }}>✕</button>
        </div>

        {loading && (
          <div style={{ padding: 48, textAlign: "center", color: "#534AB7", fontFamily: "var(--font-mono)", fontSize: 12 }}>
            Analyzing recent overrides and generating refinements...
            <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 8 }}>Takes 10–20 seconds.</div>
          </div>
        )}

        {error && (
          <div style={{ padding: 16, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, color: "#dc2626", fontSize: 13, marginTop: 12 }}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>Refinement failed</div>
            <div style={{ fontSize: 12 }}>{error}</div>
          </div>
        )}

        {!loading && !error && data && (
          <>
            <div style={{ marginTop: 16, padding: 12, background: "var(--gray-50, #f9fafb)", borderRadius: 8, borderLeft: "3px solid var(--gray-300, #d1d5db)" }}>
              <div style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", textTransform: "uppercase", marginBottom: 4 }}>Current question</div>
              <div style={{ fontSize: 13, color: "var(--text-primary)", lineHeight: 1.5 }}>{data.currentQuestion}</div>
            </div>

            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", textTransform: "uppercase", marginBottom: 8 }}>
                Proposed refinements ({data.variants?.length || 0})
              </div>
              {(data.variants || []).map((v, i) => {
                const isSelected = selected === i;
                const vs = variantStyles[i] || variantStyles[0];
                return (
                  <div key={i} onClick={() => setSelected(i)} style={{
                    padding: 14, border: `1px solid ${isSelected ? "#534AB7" : "var(--border)"}`,
                    borderRadius: 8, marginBottom: 10, cursor: "pointer",
                    background: isSelected ? "#FAFAFF" : "var(--white)",
                    transition: "all 0.15s",
                  }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                      <div style={{
                        width: 16, height: 16, borderRadius: "50%", border: `1.5px solid ${isSelected ? "#534AB7" : "var(--border)"}`,
                        flexShrink: 0, marginTop: 2, display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        {isSelected && <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#534AB7" }} />}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: 6 }}>
                          <span style={{
                            fontSize: 9, fontFamily: "var(--font-mono)", fontWeight: 600, padding: "2px 6px", borderRadius: 3,
                            background: vs.bg, color: vs.color, border: `1px solid ${vs.border}`,
                            textTransform: "uppercase", letterSpacing: "0.04em",
                          }}>{variantLabel(i, data.variants.length)}</span>
                        </div>
                        <div style={{ fontSize: 13, color: "var(--text-primary)", lineHeight: 1.5, fontWeight: 500, marginBottom: 8 }}>{v.question}</div>
                        {v.reasoning && (
                          <div style={{ fontSize: 11, color: "#534AB7", lineHeight: 1.5, marginBottom: 4 }}>
                            <strong style={{ color: "#3C3489" }}>Why it changed: </strong>{v.reasoning}
                          </div>
                        )}
                        {v.risk && (
                          <div style={{ fontSize: 11, color: "#92400e", lineHeight: 1.5 }}>
                            <strong style={{ color: "#78350f" }}>Risk: </strong>{v.risk}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {data.overridesUsed && data.overridesUsed.length > 0 && (
              <div style={{ marginTop: 4, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
                <button onClick={() => setShowExamples(s => !s)} style={{
                  background: "none", border: "none", cursor: "pointer", padding: 0,
                  display: "inline-flex", alignItems: "center", gap: 6,
                  fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--text-secondary)",
                }}>
                  <ChevronIcon open={showExamples} />
                  {showExamples ? "Hide" : "Show"} {data.overridesUsed.length} override example{data.overridesUsed.length !== 1 ? "s" : ""} used
                </button>
                {showExamples && (
                  <div style={{ marginTop: 10 }}>
                    {data.overridesUsed.map((ov, i) => (
                      <div key={i} style={{ padding: 10, background: "var(--gray-50, #f9fafb)", borderRadius: 6, marginBottom: 6, fontSize: 11, fontFamily: "var(--font-mono)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <span style={{ color: "var(--text-secondary)", fontWeight: 500 }}>{ov.doc_id}</span>
                          <span style={{ color: "var(--text-tertiary)", fontSize: 10 }}>{ov.reviewed_at ? new Date(ov.reviewed_at).toLocaleDateString() : ""}</span>
                        </div>
                        <div style={{ color: "var(--text-tertiary)", marginBottom: 2 }}>
                          AI said <strong style={{ color: ov.ai_decision === "FAIL" ? "#dc2626" : "#16a34a" }}>{ov.ai_decision}</strong> → reviewer said <strong style={{ color: ov.reviewer_decision === "OVERRIDE_PASS" ? "#16a34a" : "#dc2626" }}>{ov.reviewer_decision === "OVERRIDE_PASS" ? "PASS" : "FAIL"}</strong>
                        </div>
                        {ov.ai_evidence && (
                          <div style={{ color: "var(--text-tertiary)", marginBottom: 2 }}>
                            <em>AI evidence:</em> "{ov.ai_evidence.length > 200 ? ov.ai_evidence.slice(0, 200) + "..." : ov.ai_evidence}"
                          </div>
                        )}
                        {ov.reviewer_comment && (
                          <div style={{ color: "var(--text-secondary)" }}>
                            <em>Reviewer comment:</em> "{ov.reviewer_comment}"
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
              <button onClick={onClose} style={{
                padding: "8px 16px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12, cursor: "pointer",
                background: "var(--white)", color: "var(--text-secondary)",
              }}>Cancel</button>
              <button
                onClick={() => selected >= 0 && onAccept(data.variants[selected].question)}
                disabled={selected < 0}
                style={{
                  padding: "8px 16px", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 500,
                  cursor: selected >= 0 ? "pointer" : "not-allowed",
                  background: selected >= 0 ? "#534AB7" : "var(--gray-200, #e5e7eb)",
                  color: selected >= 0 ? "white" : "var(--text-tertiary)",
                }}>
                Use selected & open editor
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════ */
export default function AdminQAChecks() {
  const { accounts } = useMsal();
  const userName = accounts?.[0]?.name || accounts?.[0]?.username || "Admin";

  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  const [modalityOptions, setModalityOptions] = useState([]);
  const [overrideCounts, setOverrideCounts] = useState({});
  const [refineThreshold, setRefineThreshold] = useState(3);
  const [refineModalCheck, setRefineModalCheck] = useState(null);

  useEffect(() => {
    getModalities()
      .then(data => setModalityOptions((data.modalities || []).filter(m => m.Active)))
      .catch(e => console.warn("Failed to load modalities:", e));
    getQACheckOverrideCounts()
      .then(data => {
        setOverrideCounts(data.counts || {});
        if (typeof data.threshold === "number") setRefineThreshold(data.threshold);
      })
      .catch(e => console.warn("Failed to load override counts:", e));
  }, []);

  const handleAcceptRefinedQuestion = (checkId, newQuestion) => {
    setChecks(prev => prev.map(c => c.Id === checkId ? { ...c, Question: newQuestion } : c));
    setRefineModalCheck(null);
    setEditingId(checkId);
    setDirty(true);
  };

  const criteriaOptions = useMemo(() => {
    const set = new Set(checks.map(c => (c.CriteriaValue || "").trim()).filter(Boolean));
    return Array.from(set).sort();
  }, [checks]);

  const loadChecks = useCallback(() => {
    setLoading(true);
    getQAChecks()
      .then(data => {
        setChecks(data.checks || []);
        setLastSaved(data.lastModified || null);
        setLoading(false);
        setDirty(false);
      })
      .catch(e => {
        alert("Failed to load QA checks: " + (e?.message || String(e)));
        setLoading(false);
      });
  }, []);

  useEffect(() => { loadChecks(); }, [loadChecks]);

  const handleSave = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      await saveQAChecks(checks, userName);
      setLastSaved(new Date().toISOString());
      setDirty(false);
    } catch (e) {
      alert("Failed to save: " + (e?.message || String(e)));
    }
    setSaving(false);
  };

  const handleToggleStatus = (id) => {
    setChecks(prev => prev.map(c =>
      c.Id === id ? { ...c, StatusValue: c.StatusValue === "Active" ? "Inactive" : "Active" } : c
    ));
    setDirty(true);
  };

  const handleEditSave = (updatedCheck) => {
    setChecks(prev => prev.map(c => c.Id === updatedCheck.Id ? updatedCheck : c));
    setEditingId(null);
    setDirty(true);
  };

  const handleAddNew = (newCheck) => {
    const maxId = Math.max(...checks.map(c => parseInt(c.Id) || 0), 0);
    const withId = { ...newCheck, Id: String(maxId + 1) };
    setChecks(prev => [...prev, withId]);
    setAddingNew(false);
    setDirty(true);
  };

  // Filter + search
  const filtered = useMemo(() => {
    let result = checks;
    if (filter === "tech") result = result.filter(c => c.AuditType === "Technologist");
    if (filter === "ip") result = result.filter(c => c.AuditType === "Interpreting_Physician");
    if (filter === "inactive") result = result.filter(c => c.StatusValue !== "Active");
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(c =>
        (c.Question || "").toLowerCase().includes(q) ||
        // The list renders DisplayQuestion, so searching has to match it too
        // or typing what you can see on screen finds nothing.
        (c.DisplayQuestion || "").toLowerCase().includes(q) ||
        (c.CriteriaValue || "").toLowerCase().includes(q) ||
        (c.Id || "").includes(q)
      );
    }
    return result;
  }, [checks, filter, search]);

  const activeCount = checks.filter(c => c.StatusValue === "Active").length;
  const inactiveCount = checks.length - activeCount;
  const techCount = checks.filter(c => c.AuditType === "Technologist").length;
  const ipCount = checks.filter(c => c.AuditType === "Interpreting_Physician").length;

  if (loading) return (
    <div className="page-body">
      <div className="skeleton" style={{ height: 400, borderRadius: 10 }} />
    </div>
  );

  return (
    <>
      {/* ── Header ── */}
      <div className="page-header">
        <div className="page-title-group">
          <div className="page-title">QA checks</div>
          <div className="page-subtitle">Manage quality assurance criteria · {checks.length} checks ({activeCount} active)</div>
        </div>
        <div className="header-actions" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {dirty && (
            <span style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "#d97706", background: "#fffbeb", padding: "3px 10px", borderRadius: 5, border: "1px solid #fde68a" }}>
              Unsaved changes
            </span>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => setShowHistory(true)} style={{ fontSize: 11 }}>
            Version history
          </button>
          {dirty && (
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving} style={{ fontSize: 11 }}>
              {saving ? "Saving..." : "Save to blob"}
            </button>
          )}
        </div>
      </div>

      {/* ── Body ── */}
      <div className="page-body">
        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
          {[
            { val: checks.length, label: "Total checks", color: "var(--text-primary)" },
            { val: activeCount, label: "Active", color: "#16a34a" },
            { val: inactiveCount, label: "Inactive", color: "var(--text-tertiary)" },
          ].map(s => (
            <div key={s.label} style={{ padding: "10px 14px", background: "var(--gray-50, #f9fafb)", borderRadius: 8, textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 500, color: s.color }}>{s.val}</div>
              <div style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", textTransform: "uppercase" }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <span style={{ position: "absolute", left: 10, color: "var(--text-tertiary)" }}><SearchIcon /></span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search checks..."
              style={{ paddingLeft: 30, width: 220, fontSize: 12, border: "1px solid var(--border)", borderRadius: 6, padding: "6px 10px 6px 30px", background: "var(--white)", color: "var(--text-primary)" }} />
          </div>
          {[
            { key: "all", label: `All (${checks.length})` },
            { key: "tech", label: `Tech (${techCount})` },
            { key: "ip", label: `IP (${ipCount})` },
            { key: "inactive", label: `Inactive (${inactiveCount})` },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)} style={{
              padding: "5px 12px", border: `1px solid ${filter === f.key ? "var(--blue-200, #bfdbfe)" : "var(--border)"}`,
              borderRadius: 6, fontSize: 11, fontFamily: "var(--font-mono)", cursor: "pointer",
              background: filter === f.key ? "var(--blue-50, #eff6ff)" : "var(--white)",
              color: filter === f.key ? "var(--blue-700, #1d4ed8)" : "var(--text-secondary)",
            }}>{f.label}</button>
          ))}
          <button onClick={() => { setAddingNew(true); setEditingId(null); }} style={{
            marginLeft: "auto", padding: "6px 14px", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 500,
            cursor: "pointer", background: "var(--blue-800, #1d4ed8)", color: "white",
          }}>+ Add check</button>
        </div>

        {/* Table */}
        <div className="card" style={{ overflow: "hidden" }}>
          {/* Add new form */}
          {addingNew && (
            <CheckEditForm check={EMPTY_CHECK} onSave={handleAddNew} onCancel={() => setAddingNew(false)} isNew modalityOptions={modalityOptions} criteriaOptions={criteriaOptions} />
            
          )}

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "var(--gray-50, #f9fafb)" }}>
                <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", textTransform: "uppercase", fontWeight: 500, width: 40 }}>Id</th>
                <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", textTransform: "uppercase", fontWeight: 500 }}>Display question</th>
                <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", textTransform: "uppercase", fontWeight: 500, width: 55 }}>Type</th>
                <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", textTransform: "uppercase", fontWeight: 500, width: 120 }}>Criteria</th>
                <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", textTransform: "uppercase", fontWeight: 500, width: 70 }}>Sources</th>
                <th style={{ padding: "8px 12px", textAlign: "center", fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", textTransform: "uppercase", fontWeight: 500, width: 50 }}>Active</th>
                <th style={{ padding: "8px 12px", width: 150 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const isInactive = c.StatusValue !== "Active";
                const isEditing = editingId === c.Id;
                return (
                  <React.Fragment key={c.Id}>
                    <tr style={{ opacity: isInactive ? 0.5 : 1, borderBottom: "1px solid var(--border)" }}
                      onMouseEnter={e => e.currentTarget.style.background = "var(--gray-50, #f9fafb)"}
                      onMouseLeave={e => e.currentTarget.style.background = ""}>
                      <td style={{ padding: "10px 12px", fontFamily: "var(--font-mono)", fontWeight: 500, color: "var(--text-tertiary)" }}>{c.Id}</td>
                      {/* The list shows the reporting wording, which is what
                          a reader of a report sees. The prompt text lives in
                          the editor. Falls back to Question when a check has
                          no display wording of its own yet. */}
                      <td style={{ padding: "10px 12px", textDecoration: isInactive ? "line-through" : "none", maxWidth: 240 }}>{c.DisplayQuestion || c.Question}</td>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{
                          fontSize: 9, fontFamily: "var(--font-mono)", fontWeight: 600, padding: "2px 6px", borderRadius: 3,
                          background: c.AuditType === "Technologist" ? "#ecfeff" : "#f5f3ff",
                          color: c.AuditType === "Technologist" ? "#0891b2" : "#7c3aed",
                          border: `1px solid ${c.AuditType === "Technologist" ? "#a5f3fc" : "#ddd6fe"}`,
                        }}>{c.AuditType === "Technologist" ? "TECH" : "IP"}</span>
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: 11, color: "var(--text-secondary)" }}>{c.CriteriaValue}</td>
                      <td style={{ padding: "10px 12px" }}><SourceBadges sources={c.RequiredSources} /></td>
                      <td style={{ padding: "10px 12px", textAlign: "center" }}>
                        <button onClick={() => handleToggleStatus(c.Id)} style={{
                          width: 36, height: 20, borderRadius: 10, cursor: "pointer", position: "relative", border: "none",
                          background: isInactive ? "var(--gray-200, #e5e7eb)" : "#16a34a", transition: "background 0.2s",
                        }}>
                          <div style={{
                            width: 16, height: 16, borderRadius: "50%", background: "white", position: "absolute", top: 2,
                            left: isInactive ? 2 : 18, transition: "left 0.2s",
                          }} />
                        </button>
                      </td>
                      <td style={{ padding: "10px 12px", whiteSpace: "nowrap", textAlign: "right" }}>
                        {(() => {
                          const oc = overrideCounts[c.Id] || 0;
                          const canRefine = oc >= refineThreshold;
                          if (oc === 0) return null;
                          return (
                            <button
                              onClick={() => canRefine && setRefineModalCheck(c)}
                              disabled={!canRefine}
                              title={canRefine
                                ? `${oc} reviewer override${oc !== 1 ? "s" : ""} — click to propose refined wording from these cases`
                                : `Needs ${refineThreshold - oc} more override${refineThreshold - oc !== 1 ? "s" : ""} before refinement is available (${oc}/${refineThreshold})`}
                              style={{
                                padding: "3px 8px", border: "1px solid", borderRadius: 5, fontSize: 10,
                                fontFamily: "var(--font-mono)", cursor: canRefine ? "pointer" : "not-allowed",
                                background: canRefine ? "#EEEDFE" : "var(--gray-50, #f9fafb)",
                                color: canRefine ? "#3C3489" : "var(--text-tertiary)",
                                borderColor: canRefine ? "#CECBF6" : "var(--border)",
                                opacity: canRefine ? 1 : 0.55,
                                marginRight: 4,
                              }}>
                              Refine ({oc})
                            </button>
                          );
                        })()}
                        <button onClick={() => setEditingId(isEditing ? null : c.Id)} style={{
                          padding: "3px 8px", border: "1px solid var(--border)", borderRadius: 5, fontSize: 10,
                          fontFamily: "var(--font-mono)", cursor: "pointer", background: isEditing ? "var(--blue-50, #eff6ff)" : "var(--white)",
                          color: isEditing ? "var(--blue-700, #1d4ed8)" : "var(--text-secondary)",
                        }}>{isEditing ? "Close" : "Edit"}</button>
                      </td>
                    </tr>
                    {isEditing && (
                      <tr><td colSpan="7" style={{ padding: 0 }}>
                        <CheckEditForm check={c} onSave={handleEditSave} onCancel={() => setEditingId(null)} modalityOptions={modalityOptions} criteriaOptions={criteriaOptions} />
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan="7" style={{ padding: 32, textAlign: "center", color: "var(--text-tertiary)", fontSize: 13 }}>
                  {search ? "No checks match your search" : "No checks found"}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Version bar */}
        <div style={{
          marginTop: 16, padding: "12px 16px", background: "var(--gray-50, #f9fafb)", borderRadius: 8,
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: dirty ? "#d97706" : "#16a34a", flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text-primary)" }}>
              {dirty ? "Unsaved changes" : "Current version"}{lastSaved ? ` · saved ${new Date(lastSaved).toLocaleString()}` : ""}
            </div>
            <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 2 }}>
              {checks.length} checks ({activeCount} active) · last modified by {userName}
            </div>
          </div>
          <button onClick={() => setShowHistory(true)} style={{
            fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--blue-700, #1d4ed8)", cursor: "pointer",
            background: "none", border: "none", textDecoration: "underline",
          }}>View history</button>
        </div>
      </div>

      {/* Version history modal */}
      {showHistory && (
        <VersionHistory onClose={() => setShowHistory(false)} onRollback={() => { setShowHistory(false); loadChecks(); }} />
      )}

      {/* Refine from overrides modal */}
      {refineModalCheck && (
        <RefineModal
          check={refineModalCheck}
          onClose={() => setRefineModalCheck(null)}
          onAccept={q => handleAcceptRefinedQuestion(refineModalCheck.Id, q)}
        />
      )}
    </>
  );
}